<template>
  <div
    class="shadow-header flex h-[var(--layout-header-height)] min-w-0 flex-row items-center bg-white px-[var(--page-gutter)]"
  >
    <NuxtLink
      to="/"
      aria-label="Перейти на главную страницу"
      class="shrink-0 cursor-pointer"
    >
      <img src="/logo.webp" class="h-[43px] w-[40px] shrink-0" />
    </NuxtLink>
    <div
      class="ml-[clamp(20px,3vw,60px)] flex min-w-0 flex-1 flex-row items-center justify-end gap-3 xl:gap-5 2xl:gap-7"
    >
      <div
        v-if="hasPdz"
        class="flex shrink-0 items-center gap-3 rounded-[12px] border border-orange/15 bg-orange/6 px-3 py-2"
        title="Просроченная дебиторская задолженность"
      >
        <span
          class="shrink-0 text-[11px] font-helvetica-700 uppercase tracking-[0.05em] text-orange"
        >
          ПДЗ
        </span>
        <span
          class="whitespace-nowrap text-[15px] leading-none font-helvetica-700 text-gray-dark"
        >
          {{ formattedPdz }}
        </span>
      </div>
      <div
        v-if="canViewGoods"
        class="relative w-full max-w-[220px] xl:max-w-[250px] 2xl:max-w-[280px]"
      >
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          placeholder="Поиск товаров..."
          class="w-full cursor-text rounded-[10px] border border-gray-dark py-2 pl-4 pr-12 text-gray-dark focus:border-transparent focus:outline-none focus:ring-2 focus:ring-orange"
          @focus="searchHistoryOpen = true"
          @blur="searchHistoryOpen = false"
          @keydown.enter.prevent="submitSearch"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-dark transition-colors hover:bg-white-gray hover:text-orange"
          aria-label="Очистить поиск"
          @mousedown.prevent
          @click="clearSearchQuery"
        >
          <span
            class="absolute h-[1.75px] w-3.5 rotate-45 rounded-full bg-current"
          ></span>
          <span
            class="absolute h-[1.75px] w-3.5 -rotate-45 rounded-full bg-current"
          ></span>
        </button>
        <button
          v-else
          type="button"
          class="absolute right-3 top-1/2 -translate-y-1/2 transform"
          @click="submitSearch"
        >
          <img src="/finder.svg" class="h-5 w-5" />
        </button>
        <SearchHistoryDropdown
          :items="searchHistory"
          :open="searchHistoryOpen"
          @select="selectSearchHistory"
        />
      </div>

      <div class="flex shrink-0 flex-row items-center gap-2">
        <NuxtLink
          v-if="canViewGoods"
          to="/favorites"
          aria-label="Избранное"
          class="group relative inline-flex h-10 w-10 items-center justify-center focus-visible:outline-none"
        >
          <SvgoIconComponentHIconStar width="23" height="23" class="text-gray-dark hover:text-orange"></SvgoIconComponentHIconStar>
          <span :class="tooltipWrapClass">
            <span :class="tooltipBubbleClass">Избранное</span>
          </span>
        </NuxtLink>
        <NuxtLink
          v-if="canViewGoods"
          to="/compare"
          aria-label="Сравнение"
          class="group relative inline-flex h-10 w-10 items-center justify-center focus-visible:outline-none"
        >
          <SvgoIconComponentHIconCompare width="22" height="22" class="text-gray-dark group-hover:text-orange"></SvgoIconComponentHIconCompare>
          <span :class="tooltipWrapClass">
            <span :class="tooltipBubbleClass">Сравнение</span>
          </span>
        </NuxtLink>
        <NuxtLink
          v-if="canViewCart"
          to="/cart"
          aria-label="Корзина"
          class="group relative inline-flex h-10 w-10 items-center justify-center focus-visible:outline-none"
        >
          <span
            class="relative inline-flex h-[23px] w-[23px] items-center justify-center"
          >
            <SvgoIconComponentHIconCart width="23" height="23" fill="currentColor" class="text-gray-dark group-hover:text-orange"></SvgoIconComponentHIconCart>
            <span v-if="cartCountNumber > 0" :class="cartBadgeClass">
              {{ cartBadgeLabel }}
            </span>
          </span>
          <span :class="tooltipWrapClass">
            <span :class="tooltipBubbleClass">Корзина</span>
          </span>
        </NuxtLink>
        <NuxtLink
          to="https://b2b.agropromshina.ru/api/docs"
          aria-label="API"
          class="group relative inline-flex h-10 w-10 items-center justify-center focus-visible:outline-none"
        >
          <SvgoIconComponentHIconApi width="23" height="23" fill="currentColor" class="text-gray-dark hover:text-orange"></SvgoIconComponentHIconApi>
          <span :class="tooltipWrapClass">
            <span :class="tooltipBubbleClass">API</span>
          </span>
        </NuxtLink>
        <NuxtLink
          to="/profile"
          aria-label="Профиль"
          class="group relative inline-flex h-10 w-10 items-center justify-center focus-visible:outline-none"
        >
          <SvgoIconComponentHIconProfile width="23" height="23" fill="currentColor" class="text-gray-dark hover:text-orange"></SvgoIconComponentHIconProfile>
          <span :class="tooltipWrapClass">
            <span :class="tooltipBubbleClass">Профиль</span>
          </span>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "#imports";
import SearchHistoryDropdown from "~/components/search/SearchHistoryDropdown.vue";
import { useCartStore } from "~/stores/cartStore";
import { useClientPdz } from "~/composables/useClientPdz";
import { useLoginResponse } from "~/composables/useLoginResponse";
import { useSearchHistory } from "~/composables/useSearchHistory";

const cartStore = useCartStore();
const route = useRoute();
const router = useRouter();
const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);
const searchHistoryOpen = ref(false);
const { hasPdz, formattedPdz, fetchPdz } = useClientPdz();
const { hydrateLoginResponse, canViewGoods, canViewCart } = useLoginResponse();
const {
  history: searchHistory,
  hydrateSearchHistory,
  addSearchQuery,
} = useSearchHistory();

const cartCountNumber = computed(() => cartStore.cartCountNumber);
const cartBadgeLabel = computed(() => {
  if (cartCountNumber.value > 99) return "99+";
  return String(cartCountNumber.value);
});
const cartBadgeClass = computed(() => {
  const isLarge = cartCountNumber.value > 99;
  const isWide = cartCountNumber.value > 9;

  return [
    "absolute -right-[9px] -top-[7px] inline-flex h-[19px] items-center justify-center rounded-full bg-orange font-helvetica-700 text-white tabular-nums shadow-[0_1px_3px_rgba(17,17,20,0.22)] ring-2 ring-white",
    isLarge
      ? "min-w-[29px] px-[6px] text-[12px] leading-none"
      : isWide
        ? "min-w-[24px] px-[5px] text-[11px] leading-none"
        : "min-w-[19px] px-1 text-[12px] leading-none",
  ].join(" ");
});
const tooltipWrapClass =
  "pointer-events-none absolute left-1/2 top-full z-20 mt-1.5 inline-flex -translate-x-1/2 translate-y-1 items-start opacity-0 transition-all duration-150 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100";
const tooltipBubbleClass =
  "relative inline-flex h-8 min-w-[79px] items-center justify-center rounded-[5px] bg-orange px-3 text-[12px] leading-none font-helvetica-400 text-white whitespace-nowrap before:absolute before:left-1/2 before:top-0 before:h-0 before:w-0 before:-translate-x-1/2 before:-translate-y-[7px] before:border-l-[9px] before:border-r-[9px] before:border-b-[8px] before:border-l-transparent before:border-r-transparent before:border-b-orange before:content-['']";
const routeSearchQuery = computed(() => {
  const query =
    route.query.q ?? route.query.search ?? route.query.search_string;
  return typeof query === "string" ? query : "";
});

const submitSearch = () => {
  const query = searchQuery.value.trim();
  if (!query) return;
  addSearchQuery(query);
  searchHistoryOpen.value = false;
  router.push({ path: "/search/any", query: { q: query } });
};

const clearSearchQuery = () => {
  searchQuery.value = "";
  searchHistoryOpen.value = true;
  void nextTick(() => searchInput.value?.focus());
};

const selectSearchHistory = (value: string) => {
  searchQuery.value = value;
  addSearchQuery(value);
  searchHistoryOpen.value = false;
  router.push({ path: "/search/any", query: { q: value } });
};

watch(
  routeSearchQuery,
  (value) => {
    searchQuery.value = value;
  },
  { immediate: true },
);

onMounted(() => {
  hydrateLoginResponse();
  hydrateSearchHistory();
  fetchPdz();
});
</script>
